import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#06b6d4', '#8b5cf6', '#ec4899', '#10b981', '#f97316'];

export default function ReportViewer() {
  const { reportType } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportInfo, setReportInfo] = useState(null);

  useEffect(() => {
    fetchReport();
    fetchReportInfo();
  }, [reportType]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportType}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportInfo = async () => {
    try {
      const response = await axios.get(`${API}/reports/list`);
      const info = response.data.find(r => r.type === reportType);
      setReportInfo(info);
    } catch (error) {
      console.error('Error fetching report info:', error);
    }
  };

  const downloadReport = () => {
    if (!reportData) return;
    
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportType}_report.json`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400">Loading report...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reports" data-testid="back-to-reports">
            <Button variant="ghost" className="text-gray-300 hover:text-cyan-400">
              <ArrowLeft size={20} className="mr-2" />
              Back to Reports
            </Button>
          </Link>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {reportInfo?.name || reportType}
            </h1>
            <p className="text-gray-400 mt-1">{reportInfo?.category} Report</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/20" data-testid="filter-button">
            <Filter size={16} className="mr-2" />
            Filters
          </Button>
          <Button
            onClick={downloadReport}
            className="bg-cyan-500 hover:bg-cyan-600"
            data-testid="download-button"
          >
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {reportData && (
        <div className="space-y-6">
          <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
            <h3 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Visual Analysis
            </h3>
            
            {reportType === 'period_comparison' ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={reportData.chart_data}>
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="profit" fill="#10b981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData.chart_data?.slice(0, 5) || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(reportData.chart_data?.slice(0, 5) || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181b',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.chart_data?.slice(0, 10) || []}>
                    <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181b',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
            <h3 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Detailed Data
            </h3>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {reportData.table_data && reportData.table_data.length > 0 &&
                      Object.keys(reportData.table_data[0]).map((key) => (
                        <TableHead key={key} className="text-gray-300 capitalize">
                          {key.replace('_', ' ')}
                        </TableHead>
                      ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.table_data?.map((row, idx) => (
                    <TableRow key={idx} className="border-white/10" data-testid={`table-row-${idx}`}>
                      {Object.values(row).map((value, cellIdx) => (
                        <TableCell key={cellIdx} className="text-gray-200">
                          {typeof value === 'number' ? value.toLocaleString() : value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
}